import type { MouseEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { showErrorNotification } from "molecules/notifications/notificationEvents";
import { openPendingTab } from "src/common/utils/openPendingTab";
import { isEwrUrlReachable } from "./isEwrUrlReachable";

// url is backend data (the report status API) — reject anything but http(s)
// before ever assigning it to location.href, e.g. a stray "javascript:" scheme.
const isHttpUrl = (value: string): boolean => {
	try {
		const { protocol } = new URL(value);
		return protocol === "http:" || protocol === "https:";
	} catch {
		return false;
	}
};

/** Checks reachability before navigating — the Ewrs DB record can outlive the actual site. */
export const useOpenEwr = () => {
	const { t } = useTranslation();
	const [isChecking, setIsChecking] = useState(false);

	const openEwr = async (url: string, target = "_blank") => {
		if (!isHttpUrl(url)) {
			showErrorNotification({
				message: t("Exopen for Excel could not be reached right now"),
			});
			return;
		}

		const pendingTab = target === "_blank" ? openPendingTab() : null;

		setIsChecking(true);
		const reachable = await isEwrUrlReachable(url);
		setIsChecking(false);

		if (reachable) {
			if (pendingTab) {
				pendingTab.navigate(url);
			} else {
				window.location.href = url;
			}
		} else {
			pendingTab?.cancel();
			showErrorNotification({
				message: t("Exopen for Excel could not be reached right now"),
			});
		}
	};

	return { openEwr, isChecking };
};

export const useEwrLinkClick = (url: string) => {
	const { openEwr, isChecking } = useOpenEwr();

	const onClick = async (event: MouseEvent<HTMLAnchorElement>) => {
		// Modified or middle click — let the browser handle it natively.
		if (
			event.metaKey ||
			event.ctrlKey ||
			event.shiftKey ||
			event.altKey ||
			event.button !== 0
		) {
			return;
		}
		event.preventDefault();
		const target = event.currentTarget.target || "_self";
		await openEwr(url, target);
	};

	return { onClick, isChecking };
};
